import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Input, Label, Row, Col, Progress } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

// Alta masiva de modelos desde un Excel (layout). El layout se descarga aqui
// mismo con el boton "Descargar layout" para mandarselo al cliente.
//
// Reglas: CODIGO, NOMBRE y EMPRESA son obligatorios; un codigo que ya existe en
// el sistema (o repetido dentro del archivo) NO se da de alta, se marca y se
// omite. Los catalogos (linea, marca, departamento, coleccion, composicion,
// proveedor) se buscan por nombre; si no vienen o no existen se usa "NA".
// COLORES y TALLAS admiten varios separados por coma; vacio = NA.
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const env = (k) => process.env[k];
const txt = (v) => String(v === undefined || v === null ? "" : v).trim();
const nu = (v) => Number(String(v ?? "").toString().replace(/[$,\s]/g, "")) || 0;
const norm = (v) => txt(v).toUpperCase();

// Columnas del layout: [encabezado, obligatorio?, ejemplo]
const COLUMNAS = [
  ["CODIGO", true, "EJEMPLO-BORRAR"],
  ["NOMBRE", true, "BLUSA DAMA"],
  ["EMPRESA", true, "CAVANNA IMPORTACION"],
  ["DESCRIPCION", false, "BLUSA MANGA LARGA"],
  ["CODIGO CLIENTE", false, "CL-001"],
  ["LINEA", false, "NA"],
  ["MARCA", false, "NA"],
  ["DEPARTAMENTO", false, "NA"],
  ["COLECCION", false, "NA"],
  ["COMPOSICION", false, "NA"],
  ["PROVEEDOR", false, "NA"],
  ["ANO", false, "2026"],
  ["COSTO USD", false, 0],
  ["COSTO MX", false, 0],
  ["VENTA", false, 0],
  ["PRECIO TIENDA", false, 0],
  ["EAN", false, "NA"],
  ["COLORES", false, "NEGRO, BLANCO"],
  ["TALLAS", false, "CH, M, G"],
  ["OBSERVACIONES", false, "NA"],
];

function ImportarArticulosModal({ abierto, cerrar, onTerminado }) {
  const [cats, setCats] = useState({});
  const [filas, setFilas] = useState([]);
  const [archivo, setArchivo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [avance, setAvance] = useState(0);

  useEffect(() => {
    if (!abierto) return;
    setFilas([]); setArchivo(""); setAvance(0);
    const pide = (url) => axios.get(url, { headers: headers() }).then((r) => r.data || []).catch(() => []);
    Promise.all([
      pide(env("REACT_APP_URL_ARTICULOS")),
      pide(env("REACT_APP_URL_LINEAS")),
      pide(env("REACT_APP_URL_MARCAS")),
      pide(env("REACT_APP_URL_FAMILIAS")),
      pide(env("REACT_APP_URL_COLECCIONES")),
      pide(env("REACT_APP_URL_COMPOSICIONES")),
      pide(env("REACT_APP_URL_PROVEEDORES")),
      pide(env("REACT_APP_URL_EMPRESAS")),
      pide(env("REACT_APP_URL_COLORES")),
      pide(env("REACT_APP_URL_TALLAS")),
    ]).then(([articulos, lineas, marcas, familias, colecciones, composiciones, proveedores, empresas, colores, tallas]) =>
      setCats({ articulos, lineas, marcas, familias, colecciones, composiciones, proveedores, empresas, colores, tallas })
    );
  }, [abierto]);

  // Busca en un catalogo por nombre (ignora mayusculas); para colores acepta
  // "codigo nombre" o solo el nombre.
  const busca = (lista, valor, campo = "name") => {
    const t = norm(valor);
    if (!t) return null;
    return (lista || []).find((x) => norm(x[campo]) === t
      || (x.codigo && norm(`${x.codigo} ${x[campo]}`) === t)
      || (x.codigo && norm(x.codigo) === t)
      || (x.alias || []).some((a) => norm(a) === t)) || null;
  };
  const oNA = (lista, valor, campo = "name") => busca(lista, valor, campo) || busca(lista, "NA", campo);

  function descargaLayout() {
    const wb = XLSX.utils.book_new();
    // Hoja 1: encabezados + un renglon de ejemplo
    const hoja = XLSX.utils.aoa_to_sheet([
      COLUMNAS.map(([c, ob]) => (ob ? `${c} *` : c)),
      COLUMNAS.map(([, , ej]) => ej),
    ]);
    hoja["!cols"] = COLUMNAS.map(([c]) => ({ wch: Math.max(14, c.length + 4) }));
    XLSX.utils.book_append_sheet(wb, hoja, "Articulos");
    // Hoja 2: como llenarlo
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ["COMO LLENAR ESTE LAYOUT"],
      [],
      ["1", "Captura un modelo por renglon, empezando en el renglon 3. El renglon 2 es un ejemplo: se puede borrar, y aunque se deje no se da de alta."],
      ["2", "Las columnas con * son obligatorias: CODIGO, NOMBRE y EMPRESA."],
      ["3", "El CODIGO no se puede repetir. Si ya existe en el sistema o se repite en el archivo, ese renglon no se da de alta."],
      ["4", "LINEA, MARCA, DEPARTAMENTO, COLECCION, COMPOSICION y PROVEEDOR se buscan por nombre; si se dejan vacios se usa NA."],
      ["5", "COLORES y TALLAS admiten varios separados por coma (ejemplo: NEGRO, BLANCO). Vacio = NA."],
      ["6", "Los importes van en numero, sin signo de pesos; si se dejan vacios quedan en 0."],
      ["7", "Todo lo que se deje vacio se guarda como NA (texto o catalogo)."],
      ["8", "No cambies los nombres de las columnas ni el orden."],
      [],
      ["CATALOGOS DISPONIBLES (copia el nombre tal cual)"],
      ["Empresas", (cats.empresas || []).map((x) => x.name).join(" | ")],
      ["Lineas", (cats.lineas || []).map((x) => x.name).join(" | ")],
      ["Marcas", (cats.marcas || []).map((x) => x.name).join(" | ")],
      ["Departamentos", (cats.familias || []).map((x) => x.name).join(" | ")],
      ["Colecciones", (cats.colecciones || []).map((x) => x.name).join(" | ")],
      ["Composiciones", (cats.composiciones || []).map((x) => x.name).join(" | ")],
      ["Proveedores", (cats.proveedores || []).map((x) => x.nombre_comercial).join(" | ")],
      ["Colores", (cats.colores || []).map((x) => (x.name)).join(" | ")],
      ["Tallas", (cats.tallas || []).map((x) => x.name).join(" | ")],
    ]), "Instrucciones");
    XLSX.writeFile(wb, "LayoutAltaArticulos.xlsx");
  }

  function leeArchivo(file) {
    if (!file) return;
    setArchivo(file.name); setAvance(0);
    const lector = new FileReader();
    lector.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const hoja = wb.Sheets[wb.SheetNames[0]];
        const crudas = XLSX.utils.sheet_to_json(hoja, { defval: "" });
        setFilas(revisa(crudas));
      } catch (err) {
        Swal.fire({ icon: "error", title: "No se pudo leer el archivo", text: "Usa el layout que se descarga de este mismo boton." });
      }
    };
    lector.readAsArrayBuffer(file);
  }

  // Valida cada renglon y deja listo el cuerpo que se va a mandar
  function revisa(crudas) {
    const dame = (f, col) => {
      const k = Object.keys(f).find((x) => norm(x).replace(/\*/g, "").trim() === col);
      return k ? txt(f[k]) : "";
    };
    const existentes = new Set((cats.articulos || []).map((a) => norm(a.codigo)));
    const vistos = new Set();
    return crudas.map((f, i) => {
      const codigo = dame(f, "CODIGO");
      const nombre = dame(f, "NOMBRE");
      const empresaTxt = dame(f, "EMPRESA");
      const fila = { renglon: i + 2, codigo, nombre, empresa: empresaTxt };
      // El renglon de ejemplo del layout se ignora aunque no lo borren
      if (!codigo && !nombre && !empresaTxt) return { ...fila, estado: "vacio" };
      if (norm(codigo).startsWith("EJEMPLO")) return { ...fila, estado: "vacio" };
      const faltan = [];
      if (!codigo) faltan.push("CODIGO");
      if (!nombre) faltan.push("NOMBRE");
      if (!empresaTxt) faltan.push("EMPRESA");
      if (faltan.length) return { ...fila, estado: "error", detalle: `Falta ${faltan.join(", ")}` };
      const empresa = busca(cats.empresas, empresaTxt);
      if (!empresa) return { ...fila, estado: "error", detalle: `La empresa "${empresaTxt}" no existe` };
      if (existentes.has(norm(codigo))) return { ...fila, estado: "duplicado", detalle: "Ya existe en el sistema" };
      if (vistos.has(norm(codigo))) return { ...fila, estado: "duplicado", detalle: "Repetido en el archivo" };
      vistos.add(norm(codigo));

      const lista = (valor, cat, campo = "name") => {
        const nombres = txt(valor).split(",").map((x) => x.trim()).filter(Boolean);
        const ids = nombres.map((n) => busca(cat, n, campo)).filter(Boolean).map((x) => x._id);
        if (ids.length) return ids;
        const na = busca(cat, "NA", campo);
        return na ? [na._id] : [];
      };
      // Lo que venga vacio se guarda como NA (los importes, como numero, en 0)
      const oNAtxt = (col) => dame(f, col) || "NA";
      // Si escribieron un catalogo que no existe se avisa, pero tambien se usa NA
      const noExisten = [];
      const cat = (col, lista, campo = "name") => {
        const valor = dame(f, col);
        const x = busca(lista, valor, campo);
        if (valor && !x) noExisten.push(col);
        return (x || busca(lista, "NA", campo))?._id;
      };
      const cuerpo = {
        codigo, nombre,
        descripcion: oNAtxt("DESCRIPCION"),
        codigoCliente: oNAtxt("CODIGO CLIENTE"),
        piezasPorCaja: 1,
        costoUSD: nu(dame(f, "COSTO USD")),
        costoMX: nu(dame(f, "COSTO MX")),
        venta: nu(dame(f, "VENTA")),
        precioTienda: nu(dame(f, "PRECIO TIENDA")),
        observaciones: oNAtxt("OBSERVACIONES"),
        ano: oNAtxt("ANO"),
        EAN: oNAtxt("EAN"),
        codigoSAT: "NA", codigoUnidad: "NA",
        lineas: cat("LINEA", cats.lineas),
        marcas: cat("MARCA", cats.marcas),
        familias: cat("DEPARTAMENTO", cats.familias),
        colecciones: cat("COLECCION", cats.colecciones),
        composiciones: cat("COMPOSICION", cats.composiciones),
        proveedores: cat("PROVEEDOR", cats.proveedores, "nombre_comercial"),
        empresas: empresa._id,
        pesoCaja: 0, tamanoCaja: "NA", vendedores: "Si", visibleInventario: "Si",
        colores: lista(dame(f, "COLORES"), cats.colores),
        tallas: lista(dame(f, "TALLAS"), cats.tallas),
      };
      const sinCat = ["lineas", "marcas", "familias", "colecciones", "composiciones", "proveedores"].filter((k) => !cuerpo[k]);
      if (sinCat.length) return { ...fila, estado: "error", detalle: `Falta dar de alta el catalogo NA en: ${sinCat.join(", ")}` };
      return { ...fila, estado: "nuevo", cuerpo, detalle: noExisten.length ? `No existe ${noExisten.join(", ")}: queda en NA` : "" };
    }).filter((f) => f.estado !== "vacio");
  }

  const nuevos = filas.filter((f) => f.estado === "nuevo");
  const duplicados = filas.filter((f) => f.estado === "duplicado");
  const errores = filas.filter((f) => f.estado === "error");

  async function guardar() {
    if (!nuevos.length) return;
    const ok = await Swal.fire({
      icon: "question", title: `Se daran de alta ${nuevos.length} modelo(s)`,
      html: duplicados.length || errores.length
        ? `<b style="color:#b45309">${duplicados.length} duplicado(s) y ${errores.length} con error se omiten.</b>`
        : "",
      showCancelButton: true, confirmButtonText: "Si, dar de alta", cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setGuardando(true);
    const fallaron = [];
    for (let i = 0; i < nuevos.length; i++) {
      try {
        await axios.post(env("REACT_APP_URL_ARTICULOS"), nuevos[i].cuerpo, { headers: headers() });
      } catch (error) {
        fallaron.push(`${nuevos[i].codigo}: ${error.response?.data?.message || error.message}`);
      }
      setAvance(Math.round(((i + 1) / nuevos.length) * 100));
    }
    setGuardando(false);
    await Swal.fire({
      icon: fallaron.length ? "warning" : "success",
      title: `${nuevos.length - fallaron.length} modelo(s) dados de alta`,
      html: fallaron.length ? `No se pudieron dar de alta:<br><small>${fallaron.slice(0, 10).join("<br>")}</small>` : "",
    });
    cerrar();
    if (onTerminado) onTerminado();
  }

  const color = { nuevo: "#15803d", duplicado: "#b45309", error: "#dc2626" };
  return (
    <Modal size="lg" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>Alta de modelos desde layout</ModalHeader>
      <ModalBody>
        <Row>
          <Col md={5}>
            <Label><b>1. Layout</b></Label>
            <div>
              <Button size="sm" color="info" onClick={descargaLayout}>
                <i className="far fa-file-excel" /> Descargar layout
              </Button>
            </div>
            <small style={{ color: "#64748b" }}>Mándaselo al cliente para que lo llene.</small>
          </Col>
          <Col md={7}>
            <Label><b>2. Sube el layout lleno</b></Label>
            <Input type="file" accept=".xlsx,.xls" onChange={(e) => leeArchivo(e.target.files[0])} />
            {archivo ? <small style={{ color: "#64748b" }}>{archivo}</small> : null}
          </Col>
        </Row>
        <br />
        {filas.length ? (
          <>
            <div style={{ display: "flex", gap: 14, marginBottom: 6, fontSize: 13 }}>
              <span style={{ color: color.nuevo, fontWeight: 700 }}>{nuevos.length} por dar de alta</span>
              <span style={{ color: color.duplicado, fontWeight: 700 }}>{duplicados.length} duplicados</span>
              <span style={{ color: color.error, fontWeight: 700 }}>{errores.length} con error</span>
            </div>
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              <Table size="sm" bordered>
                <thead>
                  <tr><th>Renglón</th><th>Código</th><th>Nombre</th><th>Empresa</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {filas.map((f) => (
                    <tr key={f.renglon}>
                      <td>{f.renglon}</td>
                      <td>{f.codigo}</td>
                      <td>{f.nombre}</td>
                      <td>{f.empresa}</td>
                      <td style={{ color: color[f.estado], fontWeight: 600 }}>
                        {f.estado === "nuevo" ? (f.detalle ? `Se dará de alta · ${f.detalle}` : "Se dará de alta") : f.detalle}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        ) : (
          <p style={{ color: "#9ca3af" }}>Sube el layout para ver qué se va a dar de alta.</p>
        )}
        {guardando ? <Progress animated value={avance}>{avance}%</Progress> : null}
      </ModalBody>
      <ModalFooter>
        <Button color="success" onClick={guardar} disabled={guardando || !nuevos.length}>
          {guardando ? "Dando de alta..." : `Dar de alta ${nuevos.length} modelo(s)`}
        </Button>
        <Button color="secondary" onClick={cerrar} disabled={guardando}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default ImportarArticulosModal;
